import styled from 'styled-components';
import React, {FC} from 'react';

import {StylableProps} from '../../lib/styleHelpers';
import {mediaBreakpointDown} from '../../lib/styleVariables';
import {fontSizesMixinsR4, paletteR4, fontSizesMixinsMobileR4} from '../../lib/styleVariablesR4';
import {ImageProps, Image} from '../common/image';
import {Icon, IconProps} from './icon';
import {SecondaryButton} from './buttonsR4';

type Size = 'small' | 'large';
type ImgSize = 'regular' | 'mini';

interface ImageTextPublicProps extends StylableProps {
  size: Size;
  type?: 'centered' | 'grid';
  iconColor?: string;
  accentColor?: string;
}

export interface ImageTextBlockProps extends ImageTextPublicProps {
  description?: string;
  imgSrc?: ImageProps['fluid'];
  imgSize?: ImgSize;
  iconName?: IconProps['name'];
  title?: string;
  buttonText?: string;
  buttonLink?: string;
  itemPosition?: 'top' | 'bottom';
}
type ItemPositionProps = Pick<ImageTextBlockProps, 'itemPosition'>;

const StyledContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  max-width: 300px;
  text-align: center;
`;

export const StyledDescription = styled.span<{hasTitle?: boolean}>`
  ${props => (props.hasTitle ? fontSizesMixinsR4.p3() : fontSizesMixinsR4.p2())};
  margin-bottom: 40px;
`;

type StyledTitleProps = {hasDescription?: boolean; accentColor?: string} & ItemPositionProps;

const getTitleMargin = (props: StyledTitleProps) => {
  if (props.hasDescription)
    return 16;

  if (props.itemPosition === 'top')
    return 40;

  return 0;
};

export const StyledTitle = styled.span<StyledTitleProps>`
  ${fontSizesMixinsR4.h4()}
  margin-bottom: ${getTitleMargin}px;
  color: ${props => props.accentColor || paletteR4.black.base};
  ${mediaBreakpointDown('tablet')} {
    ${fontSizesMixinsMobileR4.h4()}
    margin-bottom: 24px;
  }
  ${mediaBreakpointDown('mobile')} {
    margin-bottom: 40px;
  }
`;

const StyledImage = styled(Image)<{imgSize?: ImgSize}>`
  width: 100%;
  height: ${props => (props.imgSize === 'mini' ? '12px' : '120px')};
  min-height: ${props => (props.imgSize === 'mini' ? '12px' : '120px')};
  margin-bottom: ${props => (props.imgSize === 'mini' ? '10px' : '40px')};

  > img,
  > picture img {
    object-fit: contain !important;
  }
`;

const StyledIcon = styled(Icon)<ItemPositionProps>`
  margin-bottom: ${props => (props.itemPosition === 'top' ? 32 : 40)}px;
  ${mediaBreakpointDown('tablet')} {
    margin-bottom: 16px;
    width: 80px;
    height: 80px;
  }
`;
const StyledSecondaryButton = styled(SecondaryButton)`
  margin-top: auto;
`;

export const ImageTextBlock: FC<ImageTextBlockProps> = props => {
  const hasButton: boolean = Boolean(props.buttonLink && props.buttonText);

  return (
    <StyledContainer className={props.className}>
      {props.imgSrc && <StyledImage fluid={props.imgSrc} imgSize={props.imgSize} />}
      {!props.imgSrc && props.iconName && (
        <StyledIcon
          useR4
          itemPosition={props.itemPosition}
          color={props.iconColor}
          size={props.itemPosition === 'top' ? 80 : 160}
          name={props.type === 'grid' ? 'checkmarkCircle' : props.iconName}
        />
      )}
      {props.title && (
        <StyledTitle
          accentColor={props.accentColor}
          itemPosition={props.itemPosition}
          hasDescription={Boolean(props.description)}
        >
          {props.title}
        </StyledTitle>
      )}
      {props.description && (
        <StyledDescription hasTitle={!!props.title}>{props.description}</StyledDescription>
      )}
      {hasButton && <StyledSecondaryButton href={props.buttonLink}>{props.buttonText}</StyledSecondaryButton>}
    </StyledContainer>
  );
};
